import {
  useDataProvider,
  useGetOne,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useEffect, useState, type ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

import type { Contact } from "../types";

/** Field values; labels live in crm.buyer.<field>.<value> */
export const BUYER_OPTIONS = {
  payment_method: [
    "cash",
    "installments",
    "mortgage",
    "eoselia",
    "certificate",
    "other",
  ],
  purpose: ["live", "invest", "family", "rent"],
  buy_timing: ["now", "months", "half_year", "thinking"],
  wishes: ["floor_low", "floor_high", "balcony", "view", "parking", "storage"],
  residence: ["local", "region", "ukraine", "abroad"],
  language: ["uk", "hu", "ro", "other"],
  contact_channel: ["call", "viber", "telegram", "whatsapp"],
  objections: [
    "expensive",
    "far",
    "waiting_build",
    "distrust",
    "comparing",
    "other",
  ],
  temperature: ["cold", "warm", "hot"],
} as const;

const TEMPERATURE_STYLE: Record<string, string> = {
  cold: "border-[#7dbde8] bg-[#e6f3fb] text-[#1f5f8b]",
  warm: "border-[#e8cb7d] bg-[#fbf3dc] text-[#7a5a00]",
  hot: "border-[#e88b7d] bg-[#fbe6e2] text-[#a82f23]",
};

const Chip = ({
  active,
  onClick,
  children,
  className,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  className?: string;
}) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={cn(
      "rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors",
      active
        ? (className ?? "border-primary bg-primary text-primary-foreground")
        : "bg-card text-muted-foreground hover:text-foreground",
    )}
  >
    {children}
  </button>
);

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid gap-1.5 sm:grid-cols-[150px_1fr] sm:items-center">
    <span className="text-xs font-semibold text-muted-foreground">{label}</span>
    <div className="flex flex-wrap items-center gap-1.5">{children}</div>
  </div>
);

/**
 * Buyer profile on the lead card: money, intentions, who the buyer is, how to
 * reach them, consent, objections, temperature. Every click saves at once.
 */
export const BuyerProfile = ({ contactId }: { contactId: Identifier }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { data: contact, refetch } = useGetOne<Contact>("contacts", {
    id: contactId,
  });
  const [draft, setDraft] = useState<Partial<Contact>>({});
  useEffect(() => setDraft({}), [contactId]);
  const value = <K extends keyof Contact>(key: K) =>
    (key in draft ? draft[key] : contact?.[key]) as Contact[K];

  const save = async (change: Partial<Contact>) => {
    if (!contact) return;
    setDraft((d) => ({ ...d, ...change }));
    try {
      await dataProvider.update("contacts", {
        id: contact.id,
        data: change,
        previousData: contact,
      });
      refetch();
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    }
  };
  const label = (field: string, option: string) =>
    translate(`crm.buyer.${field}.${option}`);
  const single = (field: keyof typeof BUYER_OPTIONS & keyof Contact) =>
    BUYER_OPTIONS[field].map((option) => (
      <Chip
        key={option}
        active={value(field) === option}
        onClick={() =>
          save({ [field]: value(field) === option ? null : option })
        }
      >
        {label(field, option)}
      </Chip>
    ));
  const multi = (field: "wishes" | "objections") =>
    BUYER_OPTIONS[field].map((option) => {
      const list = (value(field) as string[] | undefined) ?? [];
      const on = list.includes(option);
      return (
        <Chip
          key={option}
          active={on}
          className={
            field === "objections"
              ? "border-[#d99b92] bg-[#f3dcd8] text-[#a82f23]"
              : undefined
          }
          onClick={() =>
            save({
              [field]: on
                ? list.filter((x) => x !== option)
                : [...list, option],
            })
          }
        >
          {label(field, option)}
        </Chip>
      );
    });
  const text = (
    field: "family" | "decision_maker" | "contact_time" | "residence_country",
    width = "w-56",
  ) => (
    <Input
      className={cn("h-8", width)}
      value={(value(field) as string | null) ?? ""}
      placeholder={translate(`crm.buyer.placeholder.${field}`)}
      onChange={(event) =>
        setDraft((d) => ({ ...d, [field]: event.target.value }))
      }
      onBlur={(event) => {
        const next = event.target.value.trim() || null;
        if (next !== (contact?.[field] ?? null)) save({ [field]: next });
      }}
    />
  );
  const money = (field: "budget_min" | "budget_max") => (
    <Input
      type="number"
      inputMode="numeric"
      min={0}
      step={1000}
      className="h-8 w-28"
      value={(value(field) as number | null) ?? ""}
      placeholder={translate(`crm.buyer.placeholder.${field}`)}
      onChange={(event) =>
        setDraft((d) => ({
          ...d,
          [field]:
            event.target.value === "" ? null : Number(event.target.value),
        }))
      }
      onBlur={(event) => {
        const next =
          event.target.value === "" ? null : Number(event.target.value);
        if (next !== (contact?.[field] ?? null)) save({ [field]: next });
      }}
    />
  );

  if (!contact) return null;
  const temperature = value("temperature");

  return (
    <section className="min-w-0 rounded-2xl border bg-card p-4 sm:p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-bold">{translate("crm.buyer.title")}</h2>
        <div className="flex gap-1.5">
          {BUYER_OPTIONS.temperature.map((option) => (
            <Chip
              key={option}
              active={temperature === option}
              className={TEMPERATURE_STYLE[option]}
              onClick={() =>
                save({ temperature: temperature === option ? null : option })
              }
            >
              {label("temperature", option)}
            </Chip>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <Row label={translate("crm.buyer.budget")}>
          {money("budget_min")}
          <span className="text-muted-foreground">–</span>
          {money("budget_max")}
          <span className="text-sm text-muted-foreground">$</span>
        </Row>
        <Row label={translate("crm.buyer.payment_method.label")}>
          {single("payment_method")}
        </Row>
        <Row label={translate("crm.buyer.purpose.label")}>
          {single("purpose")}
        </Row>
        <Row label={translate("crm.buyer.buy_timing.label")}>
          {single("buy_timing")}
        </Row>
        <Row label={translate("crm.buyer.wishes.label")}>{multi("wishes")}</Row>
        <Row label={translate("crm.buyer.residence.label")}>
          {single("residence")}
          {value("residence") === "abroad"
            ? text("residence_country", "w-36")
            : null}
        </Row>
        <Row label={translate("crm.buyer.family")}>{text("family")}</Row>
        <Row label={translate("crm.buyer.decision_maker")}>
          {text("decision_maker")}
        </Row>
        <Row label={translate("crm.buyer.language.label")}>
          {single("language")}
        </Row>
        <Row label={translate("crm.buyer.contact_channel.label")}>
          {single("contact_channel")}
          {text("contact_time", "w-40")}
        </Row>
        <Row label={translate("crm.buyer.objections.label")}>
          {multi("objections")}
        </Row>
        <Row label={translate("crm.buyer.consent")}>
          <Switch
            checked={value("marketing_consent") === true}
            onCheckedChange={(checked) => save({ marketing_consent: checked })}
          />
          <span className="text-xs text-muted-foreground">
            {contact.marketing_consent && contact.consent_at
              ? translate("crm.buyer.consent_since", {
                  date: new Date(contact.consent_at).toLocaleDateString(
                    "uk-UA",
                  ),
                })
              : translate("crm.buyer.consent_hint")}
          </span>
        </Row>
      </div>
    </section>
  );
};
